/**
 * ギフトカード/プリペイドカード決済コンポーネント
 */

import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import {
  CardGiftcard as GiftCardIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const GiftCardPayment = ({
  orderId,
  orderTotal,
  storeId,
  onPaymentComplete,
  onCancel,
}) => {
  // State
  const [cardCode, setCardCode] = useState("");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [cardInfo, setCardInfo] = useState(null);
  const [useAmount, setUseAmount] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // カード残高確認
  const handleCheckCard = useCallback(async () => {
    if (!cardCode.trim()) {
      setError("カードコードを入力してください");
      return;
    }

    setIsChecking(true);
    setError(null);
    setCardInfo(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/gift-cards/check/${cardCode.toUpperCase()}?storeId=${storeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "カードの確認に失敗しました");
      }

      setCardInfo(data.card);

      // デフォルト使用額を設定（残高と注文額の小さい方）
      const defaultAmount = Math.min(data.card.balance, orderTotal);
      setUseAmount(defaultAmount.toString());
    } catch (err) {
      setError(err.message);
    } finally {
      setIsChecking(false);
    }
  }, [cardCode, storeId, orderTotal]);

  // ギフトカード使用
  const handleUseCard = useCallback(async () => {
    const amount = parseInt(useAmount, 10);

    if (!amount || amount <= 0) {
      setError("使用金額を入力してください");
      return;
    }

    if (amount > cardInfo.balance) {
      setError(`残高が不足しています（残高: ¥${cardInfo.balance.toLocaleString()}）`);
      return;
    }

    if (amount > orderTotal) {
      setError(`使用金額は注文金額（¥${orderTotal.toLocaleString()}）以下にしてください`);
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/gift-cards/use", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          code: cardCode.toUpperCase(),
          pin: pin || undefined,
          amount,
          orderId,
          storeId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "カードの使用に失敗しました");
      }

      setResult({
        amountUsed: data.amountUsed,
        remainingBalance: data.remainingBalance,
      });
      setIsComplete(true);

      // 親コンポーネントに通知
      if (onPaymentComplete) {
        onPaymentComplete({
          method: "giftcard",
          amount: data.amountUsed,
          cardCode: cardCode.toUpperCase(),
          remainingBalance: data.remainingBalance,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [cardCode, pin, useAmount, cardInfo, orderId, storeId, orderTotal, onPaymentComplete]);

  // 完了画面
  if (isComplete && result) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            ギフトカード決済完了
          </Typography>
          <Typography variant="h4" color="primary" sx={{ my: 2 }}>
            ¥{result.amountUsed.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            カード残高: ¥{result.remainingBalance.toLocaleString()}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <GiftCardIcon sx={{ fontSize: 32, mr: 1, color: "primary.main" }} />
        <Typography variant="h6">ギフトカード決済</Typography>
      </Box>

      {/* 注文金額 */}
      <Alert severity="info" sx={{ mb: 3 }}>
        お支払い金額: ¥{orderTotal.toLocaleString()}
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* カード確認フォーム */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="カードコード"
            placeholder="GC-XXXX-XXXX-XXXX"
            value={cardCode}
            onChange={(e) => setCardCode(e.target.value.toUpperCase())}
            disabled={isChecking || isProcessing || !!cardInfo}
            InputProps={{
              endAdornment: !cardInfo && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCheckCard}
                    disabled={isChecking || !cardCode.trim()}
                  >
                    {isChecking ? <CircularProgress size={20} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          {!cardInfo && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleCheckCard}
              disabled={isChecking || !cardCode.trim()}
              sx={{ height: "56px" }}
            >
              {isChecking ? <CircularProgress size={24} /> : "残高確認"}
            </Button>
          )}
        </Grid>
      </Grid>

      {/* カード情報表示 */}
      {cardInfo && (
        <Card sx={{ mt: 3, bgcolor: "grey.50" }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                カード情報
              </Typography>
              <Chip
                label={cardInfo.type === "gift" ? "ギフトカード" : "プリペイド"}
                color={cardInfo.isUsable ? "success" : "error"}
                size="small"
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  カードコード
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {cardInfo.code}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  残高
                </Typography>
                <Typography variant="h5" color="primary.main" fontWeight="bold">
                  ¥{cardInfo.balance.toLocaleString()}
                </Typography>
              </Grid>
              {cardInfo.expiresAt && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    有効期限: {new Date(cardInfo.expiresAt).toLocaleDateString("ja-JP")}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {!cardInfo.isUsable && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                このカードは現在使用できません（ステータス: {cardInfo.status}）
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* 使用金額入力 */}
      {cardInfo && cardInfo.isUsable && (
        <>
          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PIN（設定されている場合）"
                type={showPin ? "text" : "password"}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                disabled={isProcessing}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPin(!showPin)}>
                        {showPin ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="使用金額"
                type="number"
                value={useAmount}
                onChange={(e) => setUseAmount(e.target.value)}
                disabled={isProcessing}
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                }}
                helperText={`最大: ¥${Math.min(cardInfo.balance, orderTotal).toLocaleString()}`}
              />
            </Grid>
          </Grid>

          {/* 残額表示 */}
          {useAmount && parseInt(useAmount, 10) > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    ギフトカード使用
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    ¥{parseInt(useAmount, 10).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    残りのお支払い
                  </Typography>
                  <Typography variant="h6" color={orderTotal - parseInt(useAmount, 10) > 0 ? "warning.main" : "success.main"}>
                    ¥{Math.max(0, orderTotal - parseInt(useAmount, 10)).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ボタン */}
          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setCardInfo(null);
                setCardCode("");
                setPin("");
                setUseAmount("");
              }}
              disabled={isProcessing}
            >
              別のカードを使う
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUseCard}
              disabled={isProcessing || !useAmount || parseInt(useAmount, 10) <= 0}
              sx={{ flex: 1 }}
            >
              {isProcessing ? (
                <CircularProgress size={24} />
              ) : (
                `¥${parseInt(useAmount, 10) || 0}を使用する`
              )}
            </Button>
          </Box>
        </>
      )}

      {/* キャンセルボタン */}
      {!cardInfo && (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button variant="text" onClick={onCancel}>
            キャンセル
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default GiftCardPayment;
